import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Building2, User, Phone, Mail, MapPin, Users, Heart, 
  ChevronRight, ChevronLeft, Check, Save, Send, AlertCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const FAIXAS_ETARIAS = [
  { key: 'bebes_0_12m', label: 'Bebés (0-12 meses)' },
  { key: 'bebes_13_24m', label: 'Bebés (13-24 meses)' },
  { key: 'criancas_3_5a', label: 'Crianças (3-5 anos)' },
  { key: 'criancas_6_10a', label: 'Crianças (6-10 anos)' },
  { key: 'jovens_11_14a', label: 'Jovens (11-14 anos)' },
  { key: 'jovens_15_18a', label: 'Jovens (15-18 anos)' },
  { key: 'adultos_18plus', label: 'Adultos (18+ anos)' },
  { key: 'idosos_65plus', label: 'Idosos (65+ anos)' },
];

const STEPS = [
  { id: 1, name: 'Identificação', icon: Building2 },
  { id: 2, name: 'Respostas Sociais', icon: Users },
  { id: 3, name: 'Utentes', icon: Heart },
  { id: 4, name: 'Confirmação', icon: Check },
];

export default function FormularioAtualizacaoPage() {
  const { linkUnico } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Data
  const [submissao, setSubmissao] = useState(null);
  const [instituicao, setInstituicao] = useState(null);
  const [campanha, setCampanha] = useState(null);
  const [valencias, setValencias] = useState([]);
  
  // Form data
  const [dadosIdentificacao, setDadosIdentificacao] = useState({
    nome: '',
    telefone: '',
    email_principal: '',
    morada: '',
    pessoa_contacto: '',
    num_tecnicos: 0,
    num_voluntarios: 0,
    situacao_financeira: 'Equilibrada'
  });
  
  const [dadosValencias, setDadosValencias] = useState([]);
  const [dadosUtentes, setDadosUtentes] = useState({});

  useEffect(() => {
    loadData();
  }, [linkUnico]);

  // Auto-save every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!submitted && submissao) {
        saveRascunho();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [submitted, submissao, dadosIdentificacao, dadosValencias, dadosUtentes]);

  const loadData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissao/${linkUnico}`);
      if (!response.ok) {
        throw new Error('Link inválido ou expirado');
      }
      
      const data = await response.json();
      setSubmissao(data.submissao);
      setInstituicao(data.instituicao);
      setCampanha(data.campanha);
      setValencias(data.valencias_disponiveis || []);
      
      // Pre-fill with current institution data
      if (data.instituicao) {
        setDadosIdentificacao({
          nome: data.instituicao.nome || '',
          telefone: data.instituicao.telefone || '',
          email_principal: data.instituicao.email_principal || '',
          morada: data.instituicao.morada || '',
          pessoa_contacto: data.instituicao.pessoa_contacto || '',
          num_tecnicos: data.instituicao.num_tecnicos || 0,
          num_voluntarios: data.instituicao.num_voluntarios || 0,
          situacao_financeira: data.instituicao.situacao_financeira || 'Equilibrada'
        });
        
        // Pre-fill valencias
        const valenciasPrefill = (data.instituicao.valencias || []).map(v => ({
          valencia_id: v.valencia_id,
          valencia_nome: v.valencia_nome || '',
          numero_utentes: v.numero_utentes || 0,
          capacidade_maxima: v.capacidade_maxima || 0
        }));
        setDadosValencias(valenciasPrefill);
      }
      
      // Pre-fill utentes from saved draft or current data
      if (data.submissao?.dados_utentes && Object.keys(data.submissao.dados_utentes).length > 0) {
        setDadosUtentes(data.submissao.dados_utentes);
      } else if (data.utentes_atuais) {
        const utentesInit = {};
        FAIXAS_ETARIAS.forEach(faixa => {
          utentesInit[`${faixa.key}_masculino`] = data.utentes_atuais[`${faixa.key}_masculino`] || 0;
          utentesInit[`${faixa.key}_feminino`] = data.utentes_atuais[`${faixa.key}_feminino`] || 0;
        });
        setDadosUtentes(utentesInit);
      }
      
      // Check if already submitted
      if (data.submissao?.status && !['Pendente', 'Rascunho'].includes(data.submissao.status)) {
        setSubmitted(true);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(error.message || 'Erro ao carregar formulário');
    } finally {
      setLoading(false);
    }
  };

  const saveRascunho = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissao/${linkUnico}/rascunho`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dados_identificacao: dadosIdentificacao,
          dados_valencias: dadosValencias,
          dados_utentes: dadosUtentes
        })
      });
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissao/${linkUnico}/submeter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dados_identificacao: dadosIdentificacao,
          dados_valencias: dadosValencias,
          dados_utentes: dadosUtentes
        })
      });
      
      if (!response.ok) throw new Error('Erro ao submeter');
      
      const data = await response.json();
      toast.success('Dados submetidos com sucesso!');
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Erro ao submeter dados');
    } finally {
      setSubmitting(false);
    }
  };

  const addValencia = (valenciaId) => {
    const valencia = valencias.find(v => v.id === valenciaId);
    if (valencia && !dadosValencias.find(v => v.valencia_id === valenciaId)) {
      setDadosValencias([...dadosValencias, {
        valencia_id: valenciaId,
        valencia_nome: valencia.nome,
        numero_utentes: 0,
        capacidade_maxima: 0
      }]);
    }
  };

  const updateValencia = (valenciaId, field, value) => {
    setDadosValencias(dadosValencias.map(v => 
      v.valencia_id === valenciaId ? { ...v, [field]: parseInt(value) || 0 } : v
    ));
  };

  const removeValencia = (valenciaId) => {
    setDadosValencias(dadosValencias.filter(v => v.valencia_id !== valenciaId));
  };

  const updateUtentes = (campo, valor) => {
    setDadosUtentes(prev => ({ ...prev, [campo]: parseInt(valor) || 0 }));
  };

  // Calculate totals
  const totalUtentesValencias = dadosValencias.reduce((sum, v) => sum + (v.numero_utentes || 0), 0);
  const totalUtentesFaixas = Object.values(dadosUtentes).reduce((sum, v) => sum + (v || 0), 0);

  const progress = (currentStep / STEPS.length) * 100;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!submissao || !instituicao) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Link Inválido</h2>
            <p className="text-slate-500">Este link de atualização não é válido ou já expirou.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Dados Submetidos!</h2>
            <p className="text-slate-500 mb-6">
              Os dados da sua instituição foram recebidos com sucesso e serão validados pela equipa da ENTRAJUDA.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 text-left">
              <p className="text-sm text-slate-600">
                <strong>Instituição:</strong> {instituicao.nome}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                <strong>Código:</strong> {instituicao.codigo_ea}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                <strong>Campanha:</strong> {campanha?.nome}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="formulario-atualizacao">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Heart className="w-8 h-8 text-[#0066CC]" />
            <span className="text-2xl font-bold text-slate-900">ENTRAJUDATA</span>
          </div>
          <h1 className="text-xl font-semibold text-slate-700 mt-4">
            Atualização de Dados - {campanha?.nome}
          </h1>
          <p className="text-slate-500 mt-1">{instituicao.nome} ({instituicao.codigo_ea})</p>
          {campanha?.data_limite && (
            <Badge variant="outline" className="mt-2">
              <Clock className="w-3 h-3 mr-1" />
              Prazo: {campanha.data_limite}
            </Badge>
          )}
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    isCompleted ? 'bg-green-500' : isActive ? 'bg-[#0066CC]' : 'bg-slate-200'
                  } transition-colors`}>
                    {isCompleted ? (
                      <Check className="w-5 h-5 text-white" />
                    ) : (
                      <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-slate-500'}`} />
                    )}
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`w-16 sm:w-24 h-1 mx-2 ${
                      isCompleted ? 'bg-green-500' : 'bg-slate-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-slate-500">
            {STEPS.map(step => (
              <span key={step.id} className={currentStep === step.id ? 'text-[#0066CC] font-medium' : ''}>
                {step.name}
              </span>
            ))}
          </div>
        </div>

        {/* Auto-save indicator */}
        {lastSaved && (
          <div className="text-center mb-4">
            <span className="text-xs text-slate-400">
              <Save className="w-3 h-3 inline mr-1" />
              Guardado automaticamente às {lastSaved.toLocaleTimeString('pt-PT')}
            </span>
          </div>
        )}

        {/* Form Steps */}
        <Card>
          <CardHeader>
            <CardTitle>{STEPS[currentStep - 1].name}</CardTitle>
            <CardDescription>
              {currentStep === 1 && "Confirme os dados de identificação da sua instituição"}
              {currentStep === 2 && "Atualize as respostas sociais e número de utentes"}
              {currentStep === 3 && "Indique o número de utentes por faixa etária e género"}
              {currentStep === 4 && "Reveja e confirme todos os dados antes de submeter"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Step 1: Identification */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Nome da Instituição</Label>
                    <Input
                      value={dadosIdentificacao.nome}
                      onChange={(e) => setDadosIdentificacao(prev => ({ ...prev, nome: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Telefone</Label>
                    <Input
                      value={dadosIdentificacao.telefone}
                      onChange={(e) => setDadosIdentificacao(prev => ({ ...prev, telefone: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={dadosIdentificacao.email_principal}
                      onChange={(e) => setDadosIdentificacao(prev => ({ ...prev, email_principal: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <Label>Morada</Label>
                  <Textarea
                    value={dadosIdentificacao.morada}
                    onChange={(e) => setDadosIdentificacao(prev => ({ ...prev, morada: e.target.value }))}
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Pessoa de Contacto</Label>
                    <Input
                      value={dadosIdentificacao.pessoa_contacto}
                      onChange={(e) => setDadosIdentificacao(prev => ({ ...prev, pessoa_contacto: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Situação Financeira</Label>
                    <Select 
                      value={dadosIdentificacao.situacao_financeira} 
                      onValueChange={(v) => setDadosIdentificacao(prev => ({ ...prev, situacao_financeira: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Deficitária">Deficitária</SelectItem>
                        <SelectItem value="Equilibrada">Equilibrada</SelectItem>
                        <SelectItem value="Excedentária">Excedentária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nº Técnicos</Label>
                    <Input
                      type="number"
                      min="0"
                      value={dadosIdentificacao.num_tecnicos}
                      onChange={(e) => setDadosIdentificacao(prev => ({ ...prev, num_tecnicos: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                  <div>
                    <Label>Nº Voluntários</Label>
                    <Input
                      type="number"
                      min="0"
                      value={dadosIdentificacao.num_voluntarios}
                      onChange={(e) => setDadosIdentificacao(prev => ({ ...prev, num_voluntarios: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Valencias */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div>
                  <Label>Adicionar Resposta Social</Label>
                  <Select onValueChange={addValencia}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma resposta social para adicionar..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {valencias.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.codigo_crm} - {v.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {dadosValencias.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">
                      Nenhuma resposta social adicionada. Selecione acima para adicionar.
                    </p>
                  ) : (
                    dadosValencias.map((val) => (
                      <div key={val.valencia_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{val.valencia_nome}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div>
                            <Label className="text-xs">Utentes</Label>
                            <Input
                              type="number"
                              min="0"
                              className="w-20"
                              value={val.numero_utentes}
                              onChange={(e) => updateValencia(val.valencia_id, 'numero_utentes', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Capacidade</Label>
                            <Input
                              type="number"
                              min="0"
                              className="w-20"
                              value={val.capacidade_maxima}
                              onChange={(e) => updateValencia(val.valencia_id, 'capacidade_maxima', e.target.value)}
                            />
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 mt-5"
                            onClick={() => removeValencia(val.valencia_id)}
                          >
                            ✕
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <div className="bg-[#0066CC]/5 rounded-lg p-4">
                  <p className="font-semibold text-[#0066CC]">
                    Total de utentes nas respostas sociais: {totalUtentesValencias}
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: Utentes by Age/Gender */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <p className="text-sm text-slate-500 mb-4">
                  Indique o número de utentes por faixa etária e género. Os totais devem corresponder aproximadamente ao total das respostas sociais.
                </p>
                
                <div className="border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-3 gap-4 p-3 bg-slate-100 font-medium text-sm">
                    <div>Faixa Etária</div>
                    <div className="text-center text-[#0066CC]">Masculino</div>
                    <div className="text-center text-[#00AA66]">Feminino</div>
                  </div>
                  {FAIXAS_ETARIAS.map(faixa => (
                    <div key={faixa.key} className="grid grid-cols-3 gap-4 p-3 border-t items-center">
                      <div className="text-sm font-medium">{faixa.label}</div>
                      <Input
                        type="number"
                        min="0"
                        className="text-center"
                        value={dadosUtentes[`${faixa.key}_masculino`] || 0}
                        onChange={(e) => updateUtentes(`${faixa.key}_masculino`, e.target.value)}
                      />
                      <Input
                        type="number"
                        min="0"
                        className="text-center"
                        value={dadosUtentes[`${faixa.key}_feminino`] || 0}
                        onChange={(e) => updateUtentes(`${faixa.key}_feminino`, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#0066CC]/5 rounded-lg p-4">
                    <p className="font-semibold text-[#0066CC]">
                      Total por faixas: {totalUtentesFaixas}
                    </p>
                  </div>
                  <div className={`rounded-lg p-4 ${Math.abs(totalUtentesFaixas - totalUtentesValencias) > 10 ? 'bg-amber-50' : 'bg-green-50'}`}>
                    <p className={`font-semibold ${Math.abs(totalUtentesFaixas - totalUtentesValencias) > 10 ? 'text-amber-600' : 'text-green-600'}`}>
                      {Math.abs(totalUtentesFaixas - totalUtentesValencias) > 10 
                        ? `Diferença de ${Math.abs(totalUtentesFaixas - totalUtentesValencias)} utentes`
                        : 'Totais consistentes ✓'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Confirmation */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Dados de Identificação</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><span className="text-slate-500">Nome:</span> {dadosIdentificacao.nome}</p>
                    <p><span className="text-slate-500">Telefone:</span> {dadosIdentificacao.telefone}</p>
                    <p><span className="text-slate-500">Email:</span> {dadosIdentificacao.email_principal}</p>
                    <p><span className="text-slate-500">Situação:</span> {dadosIdentificacao.situacao_financeira}</p>
                    <p><span className="text-slate-500">Técnicos:</span> {dadosIdentificacao.num_tecnicos}</p>
                    <p><span className="text-slate-500">Voluntários:</span> {dadosIdentificacao.num_voluntarios}</p>
                  </div>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Respostas Sociais ({dadosValencias.length})</h4>
                  <div className="space-y-1">
                    {dadosValencias.map(v => (
                      <p key={v.valencia_id} className="text-sm">
                        {v.valencia_nome}: <span className="font-medium">{v.numero_utentes}</span> utentes
                      </p>
                    ))}
                  </div>
                  <p className="font-semibold mt-3 text-[#0066CC]">Total: {totalUtentesValencias} utentes</p>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Utentes por Faixa Etária</h4>
                  <p className="text-sm">Total: <span className="font-semibold">{totalUtentesFaixas}</span> pessoas</p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    Ao submeter, os dados serão analisados pela equipa da ENTRAJUDA. 
                    Alterações significativas podem requerer validação adicional.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(s => Math.max(1, s - 1))}
                disabled={currentStep === 1}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
              
              <Button
                variant="outline"
                onClick={saveRascunho}
              >
                <Save className="w-4 h-4 mr-1" />
                Guardar
              </Button>
              
              {currentStep < 4 ? (
                <Button
                  onClick={() => setCurrentStep(s => Math.min(4, s + 1))}
                  className="bg-[#0066CC] hover:bg-[#0052a3]"
                >
                  Seguinte
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-[#00AA66] hover:bg-[#008855]"
                >
                  {submitting ? 'A submeter...' : 'Submeter Dados'}
                  <Send className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
